import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Settings, Cpu, CheckCircle, XCircle, AlertTriangle, ChevronDown,
  ChevronUp, Save, TestTube, Eye, EyeOff, RefreshCw, Sparkles,
  Server, Key, Sliders, Globe, FileJson, HelpCircle
} from 'lucide-react';

// API Functions (token passed as parameter)
const fetchProviders = async (token) => {
  const res = await fetch('/api/ai/providers', { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error('Failed to fetch providers');
  return await res.json();
};

const fetchSettings = async (token) => {
  const res = await fetch('/api/ai/settings', { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error('Failed to fetch settings');
  return await res.json();
};

const updateProvider = async (name, settings, token) => {
  const res = await fetch(`/api/ai/settings/${name}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(settings)
  });
  if (!res.ok) throw new Error('Failed to update provider');
  return await res.json();
};

const testProvider = async (name, token) => {
  const res = await fetch(`/api/ai/settings/${name}/test`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error('Failed to test provider');
  return await res.json();
};

const fetchActiveProvider = async (token) => {
  const res = await fetch('/api/ai/active', { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error('Failed to fetch active provider');
  return await res.json();
};

export default function AISettings({ isOpen, onClose }) {
  const { token } = useAuth();
  const [providers, setProviders] = useState([]);
  const [settings, setSettings] = useState([]);
  const [activeProvider, setActiveProvider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showKey, setShowKey] = useState({});

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [providerTypes, allSettings, active] = await Promise.all([
        fetchProviders(token),
        fetchSettings(token),
        fetchActiveProvider(token)
      ]);
      setProviders(providerTypes);
      setSettings(allSettings);
      setActiveProvider(active);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProvider = async (providerName, currentActive) => {
    setSaving(true);
    setError(null);
    try {
      await updateProvider(providerName, { isActive: !currentActive }, token);
      await loadData();
      setSuccess(`${providerName} ${!currentActive ? 'activated' : 'deactivated'} successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestProvider = async (providerName) => {
    setTesting({ ...testing, [providerName]: true });
    setError(null);
    try {
      const result = await testProvider(providerName, token);
      if (result.success) {
        setSuccess(`${providerName} connection successful! Response: ${result.response.substring(0, 50)}...`);
      } else {
        setError(`${providerName} test failed: ${result.error}`);
      }
    } catch (err) {
      setError(`Test failed: ${err.message}`);
    } finally {
      setTesting({ ...testing, [providerName]: false });
      setTimeout(() => setSuccess(null), 5000);
    }
  };

  const handleSaveProvider = async (providerName, formData) => {
    setSaving(true);
    setError(null);
    try {
      await updateProvider(providerName, {
        apiKey: formData.apiKey,
        baseUrl: formData.baseUrl,
        modelName: formData.modelName,
        temperature: parseFloat(formData.temperature),
        maxTokens: parseInt(formData.maxTokens),
        timeoutMs: parseInt(formData.timeoutMs),
        customHeaders: formData.customHeaders ? JSON.parse(formData.customHeaders) : null,
        customParams: formData.customParams ? JSON.parse(formData.customParams) : null
      }, token);
      await loadData();
      setSuccess(`${providerName} settings saved successfully`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Settings size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">AI Provider Settings</h2>
              <p className="text-blue-100 text-sm">Configure multiple AI providers for document extraction</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-2">
            <XCircle size={28} />
          </button>
        </div>

        {/* Status Banner */}
        {activeProvider && (
          <div className="bg-green-50 border-b border-green-200 p-3 flex items-center gap-3">
            <CheckCircle size={20} className="text-green-600" />
            <span className="text-green-800 font-medium">
              Active Provider: <span className="capitalize">{activeProvider.provider}</span>
              {' • '}
              Model: {activeProvider.model}
            </span>
          </div>
        )}

        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 m-4 rounded">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}
        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 m-4 rounded">
            <div className="flex items-start gap-3">
              <CheckCircle size={20} className="text-green-500 shrink-0 mt-0.5" />
              <p className="text-green-700">{success}</p>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={32} className="text-blue-600 animate-spin" />
              <span className="ml-3 text-slate-600">Loading providers...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {settings.map((setting) => {
                const config = setting.config || {};
                const isExpanded = expanded === setting.provider_name;
                const isActive = setting.is_active;
                const isTesting = testing[setting.provider_name];
                const hasKey = setting.has_key;

                return (
                  <div
                    key={setting.provider_name}
                    className={`border-2 rounded-xl overflow-hidden transition-all ${
                      isActive ? 'border-green-500 bg-green-50/30' : 'border-slate-200'
                    }`}
                  >
                    {/* Header Row */}
                    <div
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                      onClick={() => setExpanded(isExpanded ? null : setting.provider_name)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${isActive ? 'bg-green-100' : 'bg-slate-100'}`}>
                          {isActive ? (
                            <Sparkles size={20} className="text-green-600" />
                          ) : (
                            <Cpu size={20} className="text-slate-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800 capitalize flex items-center gap-2">
                            {config.displayName || setting.provider_name}
                            {setting.provider_name === 'custom' && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                Advanced
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-slate-500">
                            {setting.model_name || config.defaultModel}
                            {config.supportsVision && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                Vision
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleProvider(setting.provider_name, isActive);
                          }}
                          disabled={saving}
                          className={`px-4 py-2 rounded-lg font-medium transition-all ${
                            isActive
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                          }`}
                        >
                          {isActive ? 'Active' : 'Activate'}
                        </button>
                        {isExpanded ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
                      </div>
                    </div>

                    {/* Expanded Form */}
                    {isExpanded && (
                      <ProviderForm
                        setting={setting}
                        config={config}
                        onSave={(data) => handleSaveProvider(setting.provider_name, data)}
                        onTest={() => handleTestProvider(setting.provider_name)}
                        isTesting={isTesting}
                        isSaving={saving}
                        showKey={showKey[setting.provider_name]}
                        onToggleKey={() => setShowKey({ ...showKey, [setting.provider_name]: !showKey[setting.provider_name] })}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 bg-slate-50 flex justify-between items-center">
          <button
            onClick={loadData}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-800"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProviderForm({ setting, config, onSave, onTest, isTesting, isSaving, showKey, onToggleKey }) {
  const [formData, setFormData] = useState({
    apiKey: '',
    baseUrl: setting.base_url || config.defaultBaseUrl || '',
    modelName: setting.model_name || config.defaultModel || '',
    temperature: setting.temperature ?? 0.1,
    maxTokens: setting.max_tokens ?? 4096,
    timeoutMs: setting.timeout_ms ?? 30000,
    customHeaders: setting.custom_headers ? JSON.stringify(setting.custom_headers, null, 2) : '',
    customParams: setting.custom_params ? JSON.stringify(setting.custom_params, null, 2) : ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isCustom = setting.provider_name === 'custom';

  return (
    <div className="border-t border-slate-200 p-4 bg-white">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* API Key */}
        <div className="md:col-span-2">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
            <Key size={14} />
            API Key
            {setting.has_key && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                Configured
              </span>
            )}
          </label>
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              value={formData.apiKey}
              onChange={(e) => handleChange('apiKey', e.target.value)}
              placeholder={setting.has_key ? '••••••••••••••••' : 'Enter your API key'}
              className="input-field pr-10"
            />
            <button
              type="button"
              onClick={onToggleKey}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showKey ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {setting.has_key
              ? 'Leave blank to keep existing key, or enter new key to update'
              : 'Enter your API key to enable this provider'}
          </p>
        </div>

        {/* Base URL */}
        <div className={isCustom ? 'md:col-span-2' : ''}>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
            <Globe size={14} />
            Base URL
          </label>
          <input
            type="text"
            value={formData.baseUrl}
            onChange={(e) => handleChange('baseUrl', e.target.value)}
            placeholder={config.defaultBaseUrl || 'https://api.example.com/v1'}
            className="input-field"
          />
        </div>

        {/* Model Name */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
            <Server size={14} />
            Model Name
          </label>
          <input
            type="text"
            value={formData.modelName}
            onChange={(e) => handleChange('modelName', e.target.value)}
            placeholder={config.defaultModel || 'model-name'}
            className="input-field"
          />
        </div>

        {/* Temperature */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
            <Sliders size={14} />
            Temperature ({formData.temperature})
          </label>
          <input
            type="range"
            min="0"
            max="2"
            step="0.1"
            value={formData.temperature}
            onChange={(e) => handleChange('temperature', e.target.value)}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-slate-500">
            <span>Precise (0)</span>
            <span>Creative (2)</span>
          </div>
        </div>

        {/* Max Tokens */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
            Max Tokens
          </label>
          <input
            type="number"
            min="100"
            max="8192"
            value={formData.maxTokens}
            onChange={(e) => handleChange('maxTokens', e.target.value)}
            className="input-field"
          />
        </div>

        {/* Timeout */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
            Timeout (ms)
          </label>
          <input
            type="number"
            min="1000"
            max="120000"
            step="1000"
            value={formData.timeoutMs}
            onChange={(e) => handleChange('timeoutMs', e.target.value)}
            className="input-field"
          />
        </div>

        {/* Custom Headers (Custom Provider) */}
        {isCustom && (
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
              <FileJson size={14} />
              Custom Headers (JSON)
            </label>
            <textarea
              value={formData.customHeaders}
              onChange={(e) => handleChange('customHeaders', e.target.value)}
              placeholder='{"X-Custom-Header": "value"}'
              className="input-field h-20 font-mono text-sm"
            />
          </div>
        )}

        {/* Custom Params (Custom Provider) */}
        {isCustom && (
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
              <HelpCircle size={14} />
              Custom Parameters (JSON)
            </label>
            <textarea
              value={formData.customParams}
              onChange={(e) => handleChange('customParams', e.target.value)}
              placeholder='{"payloadTemplate": "openai-like", "responsePath": "choices.0.message.content"}'
              className="input-field h-24 font-mono text-sm"
            />
            <p className="text-xs text-slate-500 mt-1">
              Supported: payloadTemplate (openai-like/gemini-like), responsePath (dot notation)
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 mt-6">
        <button
          onClick={() => onSave(formData)}
          disabled={isSaving}
          className="btn-primary"
        >
          {isSaving ? (
            <RefreshCw size={18} className="animate-spin" />
          ) : (
            <Save size={18} />
          )}
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
        <button
          onClick={onTest}
          disabled={isTesting}
          className="btn-secondary"
        >
          {isTesting ? (
            <RefreshCw size={18} className="animate-spin" />
          ) : (
            <TestTube size={18} />
          )}
          {isTesting ? 'Testing...' : 'Test Connection'}
        </button>
      </div>
    </div>
  );
}
